/**
 * 移动端底部导航栏
 * Design spec: docs/mobile-design/01-feed/mobile-feed-design.html
 */

import { Home, Compass, Sparkles, Bell, CircleDot } from 'lucide-react'
import { useT } from '@/i18n'

interface NavItem {
  id: string
  icon: typeof Home
  /** i18n key for the label */
  labelKey: string
}

const NAV_ITEMS: NavItem[] = [
  { id: 'feed', icon: Home, labelKey: 'navigation.main.feed' },
  { id: 'discover', icon: Compass, labelKey: 'navigation.bottomNav.discover' },
  { id: 'myagent', icon: Sparkles, labelKey: 'navigation.agent.myAgent' },
  { id: 'circles', icon: CircleDot, labelKey: 'navigation.main.circles' },
  { id: 'alerts', icon: Bell, labelKey: 'navigation.bottomNav.alerts' },
]

interface MobileBottomNavProps {
  /** 当前激活项 */
  activeItem?: string
  /** 导航回调 */
  onNavigate?: (page: string) => void
  /** 底部安全区域高度 */
  safeAreaBottom?: number
}

/* Shimmer keyframes injected once via <style> */
const shimStyle = `
@keyframes bnav-shim {
  0%, 100% { transform: translateX(-120%); }
  50% { transform: translateX(120%); }
}
`

export function MobileBottomNav({
  activeItem = 'feed',
  onNavigate,
  safeAreaBottom = 0,
}: MobileBottomNavProps) {
  const t = useT()
  return (
    <>
      <style>{shimStyle}</style>
      <nav
        className="fixed bottom-0 left-0 right-0 z-50"
        style={{
          background: 'var(--y-glass-nav)',
          backdropFilter: 'blur(28px) saturate(1.5)',
          WebkitBackdropFilter: 'blur(28px) saturate(1.5)',
          boxShadow: 'var(--y-shadow-nav)',
          paddingBottom: `max(${safeAreaBottom}px, env(safe-area-inset-bottom, 0px))`,
        }}
      >
        {/* .bnav-in */}
        <div
          className="flex items-end justify-around"
          style={{ height: 56, padding: '0 2px' }}
        >
          {NAV_ITEMS.map(({ id, icon: Icon, labelKey }) => {
            const isActive = activeItem === id
            const isCenter = id === 'myagent'
            const label = t(labelKey)

            if (isCenter) {
              return (
                <button
                  key={id}
                  onClick={() => onNavigate?.(id)}
                  className="flex flex-col items-center"
                  style={{ flex: 1, marginTop: -16 }}
                  aria-label={label}
                >
                  {/* FAB button */}
                  <div
                    className="relative flex items-center justify-center overflow-hidden active:scale-[0.88] transition-transform"
                    style={{
                      width: 46,
                      height: 46,
                      borderRadius: 15,
                      background:
                        'linear-gradient(140deg, var(--y-accent), var(--y-accent-secondary))',
                      boxShadow: 'var(--y-shadow-fab)',
                    }}
                  >
                    <Icon
                      /* TODO: design spec uses --act (#fff light / #1A1612 dark), --y-accent-text needs light theme fix */
                      style={{ width: 22, height: 22, color: '#fff' }}
                      strokeWidth={2}
                    />
                    {/* Shimmer overlay */}
                    <div
                      className="absolute inset-0 pointer-events-none"
                      style={{
                        background:
                          'linear-gradient(105deg, transparent 38%, rgba(255,255,255,0.22) 50%, transparent 62%)',
                        animation: 'bnav-shim 3.5s ease-in-out infinite',
                      }}
                    />
                  </div>
                  {/* FAB label */}
                  <span
                    style={{
                      fontSize: 9,
                      fontWeight: 600,
                      color: 'var(--y-accent)',
                      marginTop: 4,
                    }}
                  >
                    {label}
                  </span>
                </button>
              )
            }

            /* Normal tab */
            return (
              <button
                key={id}
                onClick={() => onNavigate?.(id)}
                className="relative flex flex-col items-center justify-center active:scale-[0.92] transition-transform"
                style={{ flex: 1, gap: 2, padding: '6px 0 8px' }}
                aria-label={label}
              >
                {/* Active dot */}
                {isActive && (
                  <span
                    className="absolute"
                    style={{
                      top: 0,
                      width: 4,
                      height: 4,
                      borderRadius: '50%',
                      background: 'var(--y-accent)',
                    }}
                  />
                )}
                <Icon
                  style={{
                    width: 22,
                    height: 22,
                    color: isActive
                      ? 'var(--y-accent)'
                      : 'var(--y-text-disabled)',
                  }}
                  strokeWidth={isActive ? 2 : 1.7}
                />
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: isActive ? 600 : 500,
                    color: isActive
                      ? 'var(--y-accent)'
                      : 'var(--y-text-disabled)',
                  }}
                >
                  {label}
                </span>
              </button>
            )
          })}
        </div>
      </nav>
    </>
  )
}
